import React, { useState } from 'react'
import questionData, { googleFormUrl } from './questionData'
import LayoutContent from '@components/LayoutContent'
import { useRNApp } from '@libs/RNApp'
import styled from 'styled-components'
import tw, { theme } from 'twin.macro'
import ArrowDownIcon from '@components/Icons/ArrowDownIcon'
import Head from 'next/head'

const LinkWithRnAppWebview = ({ children, ...props }) => {
  const handleOnClick = (e) => {
    e.preventDefault()
    if (window.RNApp && window.RNApp.webview) {
      window.RNApp.webview(props.href)
    } else {
      window.open(props.href)
    }
  }

  return (
    <a {...props} onClick={handleOnClick}>
      {children}
    </a>
  )
}

const PageContainer = styled.div`
  margin-top: 32px;
  padding: 0 16px;
  margin-bottom: 160px;
  ${tw`text-h5`}
  a {
    ${tw`text-primary-indigo`};
  }

  ul {
    list-style-type: disc;
    margin-left: 30px;
  }

  @media (min-width: ${theme`screens.md`}) {
    margin: 0 auto;
    max-width: ${theme`screens.md`};
    margin-bottom: 100px;
  }
`

const PageTitle = styled.div`
  ${tw`text-h2 font-semibold`}
  @media (min-width: ${theme`screens.md`}) {
    ${tw`text-d2 font-semibold`}
  }
`

const SectionContainer = styled.div`
  padding: 20px 0;
  overflow: hidden;
  @media (min-width: ${theme`screens.md`}) {
    padding: 24px 0;
  }
`

const SectionTitle = styled.div`
  ${tw`text-article font-semibold`}
  display: flex;
  align-items: center;
  cursor: pointer;
  @media (min-width: ${theme`screens.md`}) {
    ${tw`text-h2 font-semibold`}
  }
`

interface QuestionContainerProps {
  isSectionOpen: boolean
}

const QuestionsContainer = styled.div<QuestionContainerProps>`
  height: ${(props) => (props.isSectionOpen ? 'auto' : '0px')};
  overflow: hidden;
`

const QuestionTitle = styled.div`
  margin-top: 16px;
  ${tw`text-h5 font-medium`}
  @media (min-width: ${theme`screens.md`}) {
    margin-top: 24px;
  }
`

const QuestionContent = styled.div`
  margin-top: 8px;
  line-height: 170%;
  margin-left: 5px;
  @media (min-width: ${theme`screens.md`}) {
    margin-left: 20px;
  }
`

const Divider = styled.div`
  height: 0.5px;
  background-color: ${theme`colors.gray.100`};
`

interface StyledArrowDownIconProps {
  isopen: boolean
}

const StyledArrowDownIcon = styled(ArrowDownIcon)<StyledArrowDownIconProps>`
  margin-left: 12px;
  transform: ${(props) => (props.isopen ? 'rotate(0deg)' : 'rotate(-90deg)')};
  transition: transform 0.2s ease-in-out;
`

const BottomHint = styled.div`
  ${tw`bg-primary-light`}
  border-radius: 16px;
  text-align: center;
  padding: 24px 16px;
  margin-top: 48px;
  @media (min-width: ${theme`screens.md`}) {
    margin-top: 64px;
    padding: 40px 16px;
  }
`

const Question = () => {
  const { isApp } = useRNApp()
  const [sectionsIsOpen, setSectionsIsOpen] = useState(new Array(questionData.length).fill(true))

  const handleSectionOnClick = (index) => {
    setSectionsIsOpen((a) => {
      const newA = [...a]
      newA.splice(index, 1, !a[index])
      return newA
    })
  }

  const handleQuestionContentOnClick = (e) => {
    const el = e.target.closest('a')
    if (el && e.currentTarget.contains(el)) {
      e.preventDefault()
      const url = el.getAttribute('href')
      if (window.RNApp && window.RNApp.webview) {
        window.RNApp.webview(url)
      } else {
        window.open(url)
      }
    }
  }

  return (
    <>
      <Head>
        <title key="title">常見問題與回報｜PopDaily 波波黛莉</title>
      </Head>
      <LayoutContent isApp={isApp} noTopAd>
        <PageContainer>
          <PageTitle>常見問題</PageTitle>
          {questionData.map((section, index) => {
            return (
              <div key={section.title}>
                <SectionContainer>
                  <SectionTitle onClick={() => handleSectionOnClick(index)}>
                    <div>{section.title}</div>
                    <StyledArrowDownIcon isopen={sectionsIsOpen[index]} />
                  </SectionTitle>
                  <QuestionsContainer isSectionOpen={sectionsIsOpen[index]}>
                    {section.questions.map((question, index) => {
                      return (
                        <div key={question.title}>
                          <QuestionTitle>
                            <span tw="mr-1">{index + 1}.</span>
                            <span>{question.title}</span>
                          </QuestionTitle>
                          <QuestionContent
                            dangerouslySetInnerHTML={{ __html: question.content }}
                            onClick={handleQuestionContentOnClick}
                          />
                        </div>
                      )
                    })}
                  </QuestionsContainer>
                </SectionContainer>
                {index !== questionData.length - 1 && <Divider />}
              </div>
            )
          })}
          <BottomHint>
            👉 若是常見問題中找不到您需要的答案，歡迎填寫{' '}
            <LinkWithRnAppWebview href={googleFormUrl}>常見問題與回報</LinkWithRnAppWebview>{' '}
            表單聯絡我們 👈
          </BottomHint>
        </PageContainer>
      </LayoutContent>
    </>
  )
}

export const getServerSideProps = () => {
  return {
    props: {},
  }
}

export default Question
